import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, TextField, MenuItem, Chip, Button,
  InputAdornment, Autocomplete, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import DateRangePicker from '../../components/common/DateRangePicker';
import db from '../../data/demoDatabase';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const statusOptions = ['All', 'Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision', 'Approved', 'Rejected', 'Disbursed', 'Closed'];

const isoDate = (d) => new Date(d).toISOString().slice(0, 10);
const formatRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;

/**
 * Sales listing page.
 * Props:
 *   - restrictToUserId: when set, only shows cases where referralOwnerId === this id.
 *     Hides the Partner/Agent autocomplete. Used for the agent self-view.
 *   - pageTitle: optional title override (default "Sales Listing")
 *   - pageSubtitle: optional subtitle override
 */
export default function SalesListing({ restrictToUserId = null, pageTitle, pageSubtitle } = {}) {
  const allCases = useMemo(() => db.cases.getAll(), []);
  const allUsers = useMemo(() => db.users.getAll(), []);

  // Partners + agents only (referrers)
  const referrers = useMemo(
    () => allUsers.filter((u) => ['partner', 'individual_agent'].includes(u.role)),
    [allUsers]
  );

  // Default date range = last 90 days
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return isoDate(d);
  }, []);
  const defaultEnd = useMemo(() => isoDate(new Date()), []);

  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedCase, setSelectedCase] = useState(null);

  // Referrer name lookup
  const referrerNameById = useMemo(() => {
    const map = {};
    allUsers.forEach((u) => { map[u.id] = u.name; });
    return map;
  }, [allUsers]);

  const filteredCases = useMemo(() => {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate + 'T23:59:59').getTime();
    return allCases.filter((c) => {
      // Hard restriction (agent self-view)
      if (restrictToUserId && c.referralOwnerId !== restrictToUserId) return false;
      // Date range
      const t = new Date(c.createdAt).getTime();
      if (t < start || t > end) return false;
      // Referrer filter (only when not restricted)
      if (!restrictToUserId && selectedReferrer && c.referralOwnerId !== selectedReferrer.id) return false;
      // Status filter
      if (statusFilter !== 'All' && c.status !== statusFilter) return false;
      // Keyword search
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const matches =
          c.applicantName?.toLowerCase().includes(s) ||
          c.productName?.toLowerCase().includes(s) ||
          c.bank?.toLowerCase().includes(s) ||
          c.status?.toLowerCase().includes(s) ||
          String(c.amount || '').includes(s) ||
          c.id?.toLowerCase().includes(s);
        if (!matches) return false;
      }
      return true;
    });
  }, [allCases, selectedReferrer, startDate, endDate, statusFilter, searchTerm, restrictToUserId]);

  // Stats
  const stats = useMemo(() => {
    const approvedStatuses = ['Approved', 'Disbursed'];
    const approved = filteredCases.filter((c) => approvedStatuses.includes(c.status));
    const disbursed = filteredCases.filter((c) => c.status === 'Disbursed');
    const totalLoan = filteredCases.reduce((sum, c) => sum + (c.amount || 0), 0);
    const totalCommission = filteredCases.reduce((sum, c) => sum + (c.commissionAmount || 0), 0);
    return {
      total: filteredCases.length,
      approved: approved.length,
      disbursed: disbursed.length,
      totalLoan,
      totalCommission,
    };
  }, [filteredCases]);

  const getProcessedDate = (row) => {
    if (row.status === 'Approved' || row.status === 'Disbursed' || row.status === 'Rejected') {
      return isoDate(row.updatedAt);
    }
    return '—';
  };

  const columns = [
    {
      field: 'id', headerName: 'Case ID', width: 130,
      renderCell: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.id}</Typography>
      ),
    },
    {
      field: 'applicantName', headerName: 'Customer Name',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.applicantName}</Typography>
          <Typography variant="caption" color="text.secondary">{row.applicantId}</Typography>
        </Box>
      ),
    },
    {
      field: 'productName', headerName: 'Product',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.productName}</Typography>
          <Chip label={row.bank} size="small" sx={{ fontSize: '0.65rem', height: 18, mt: 0.25 }} />
        </Box>
      ),
    },
    {
      field: 'amount', headerName: 'Loan Amount', align: 'right', width: 140,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>
          {formatRM(row.amount)}
        </Typography>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 150,
      renderCell: (row) => <StatusBadge status={row.status} />,
    },
    {
      field: 'referralOwnerId', headerName: 'Referred By', width: 170,
      renderCell: (row) => {
        if (!row.referralOwnerId) return <Typography variant="caption" color="text.secondary">—</Typography>;
        const name = referrerNameById[row.referralOwnerId] || row.referralOwnerId;
        return (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const ref = referrers.find((r) => r.id === row.referralOwnerId);
              if (ref) setSelectedReferrer(ref);
            }}
            sx={{
              textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
              color: PRIMARY, minWidth: 0, padding: '2px 6px',
              '&:hover': { backgroundColor: 'rgba(27,54,93,0.08)' },
            }}
          >
            {name}
          </Button>
        );
      },
    },
    {
      field: 'createdAt', headerName: 'Created', width: 110,
      renderCell: (row) => <Typography variant="caption">{isoDate(row.createdAt)}</Typography>,
    },
    {
      field: 'processedDate', headerName: 'Processed', width: 110,
      renderCell: (row) => <Typography variant="caption">{getProcessedDate(row)}</Typography>,
    },
  ];

  const clearFilters = () => {
    setSelectedReferrer(null);
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setSearchTerm('');
    setStatusFilter('All');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            {pageTitle || 'Sales Listing'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pageSubtitle || (restrictToUserId
              ? 'Your own sales cases'
              : 'View and filter all partner / agent sales cases across the business')}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', borderColor: PRIMARY, color: PRIMARY }}>
          Export
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          {!restrictToUserId && (
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={referrers}
                value={selectedReferrer}
                onChange={(_, v) => setSelectedReferrer(v)}
                getOptionLabel={(opt) => `${opt.name} (${opt.id})`}
                renderInput={(params) => (
                  <TextField {...params} label="Partner / Agent"
                    placeholder="All referrers"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18 }} /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <DateRangePicker
              value={{ start: startDate, end: endDate }}
              onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth select size="small" label="Status"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" placeholder="Search customer, product, amount..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              }} />
          </Grid>
        </Grid>
        {(selectedReferrer || statusFilter !== 'All' || searchTerm || startDate !== defaultStart || endDate !== defaultEnd) && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">Active filters:</Typography>
            {selectedReferrer && (
              <Chip size="small" label={`Referrer: ${selectedReferrer.name}`} onDelete={() => setSelectedReferrer(null)} />
            )}
            {statusFilter !== 'All' && (
              <Chip size="small" label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter('All')} />
            )}
            {searchTerm && (
              <Chip size="small" label={`Search: ${searchTerm}`} onDelete={() => setSearchTerm('')} />
            )}
            <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
              Clear all
            </Button>
          </Box>
        )}
      </Paper>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Total Cases" value={stats.total} icon={AssignmentIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Approved" value={stats.approved} icon={CheckCircleIcon} color="#00B894" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Disbursed" value={stats.disbursed} icon={PaidIcon} color="#2E7D32" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Total Loan" value={formatRM(stats.totalLoan)} icon={AccountBalanceWalletIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard title="Commission" value={formatRM(stats.totalCommission)} icon={PaidIcon} color="#7B1FA2" />
        </Grid>
      </Grid>

      {/* Data table */}
      <DataTable
        columns={columns} rows={filteredCases}
        title={selectedReferrer ? `Cases for ${selectedReferrer.name}` : 'All Sales Cases'}
        searchable={false} pageSize={15}
        onRowClick={(row) => setSelectedCase(row)}
        exportable
      />

      {/* Case detail dialog */}
      <Dialog open={!!selectedCase} onClose={() => setSelectedCase(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Case Details</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedCase?.id}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCase && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Customer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.applicantName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusBadge status={selectedCase.status} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Product</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.productName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bank</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.bank}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>{formatRM(selectedCase.amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Commission</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: SECONDARY }}>{formatRM(selectedCase.commissionAmount)}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Referral Code</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.referralCode || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Referred By</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedCase.referralOwnerId ? referrerNameById[selectedCase.referralOwnerId] || selectedCase.referralOwnerId : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedCase.createdAt).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedCase.updatedAt).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCase(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
